import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { getApp } from "../../app";
import { getDashboardChartData } from "../../api.service";
import { DashboardDetailResponse } from "../../../../../shared/models/dashboard";
import {
  DASHBOARD_DATA_STORAGE_PREFIX,
  DashboardDataViewTransfer,
} from "./dashboard-data-view.model";

@Component({
    selector: "app-dashboard-data-view",
    templateUrl: "./dashboard-data-view.component.html",
    styleUrls: ["./dashboard-data-view.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardDataViewComponent implements OnInit, AfterViewChecked {
  @ViewChild("chartContainer") chartContainer?: ElementRef<HTMLElement>;

  transfer: DashboardDataViewTransfer | null = null;
  response: DashboardDetailResponse | null = null;
  error = "";
  loading = true;
  chartRendered = false;
  currentTime = Date.now();

  app = getApp((app) => {
    this.app = app;
  });

  constructor(private route: ActivatedRoute) {}

  async ngOnInit() {
    this.app.spinner.showSpinner();

    const transferId = this.route.snapshot.queryParamMap.get("transfer");

    try {
      if (!transferId) {
        this.setError("No dashboard data request was provided.");
        return;
      }

      const storageKey = `${DASHBOARD_DATA_STORAGE_PREFIX}${transferId}`;
      const rawTransfer = localStorage.getItem(storageKey);
      this.transfer = rawTransfer ? JSON.parse(rawTransfer) : null;
      localStorage.removeItem(storageKey);

      if (!this.transfer) {
        this.setError(
          "The dashboard request is no longer available. Open this view again from the dashboard."
        );
        return;
      }

      if (this.transfer.request.dataType === "task") {
        await this.app.users.getAllUserNames();
      }

      await this.loadPage(1);

      if (!this.response) {
        this.setError("The dashboard data service returned no data.");
        return;
      }
    } catch (error) {
      console.error("Error while loading dashboard detail data:", error);
      this.setError("The dashboard data could not be loaded.");
      return;
    } finally {
      this.loading = false;
      this.app.spinner.hideSpinner();
    }

    this.renderChartWhenReady();
  }

  ngAfterViewChecked() {
    this.renderChartWhenReady();
  }

  get columns() {
    return this.transfer?.columns ?? [];
  }

  get chartSummary() {
    return this.transfer?.chartSummary;
  }

  getSummaryPercentage(value: number) {
    const total = this.chartSummary?.total ?? 0;
    return total > 0 ? (value / total) * 100 : 0;
  }

  async loadPage(page: number) {
    if (!this.transfer) {
      return;
    }
    this.transfer.request.page = page;
    this.transfer.request.pageSize = 25;
    this.response = await getDashboardChartData(
      this.app.customers.expectCurrent,
      this.transfer.request
    );
  }

  private setError(message: string) {
    this.error = message;
  }

  private async renderChartWhenReady() {
    if (
      this.chartRendered ||
      this.transfer?.chartImage ||
      !this.response?.chartHtml ||
      !this.chartContainer
    ) {
      return;
    }

    const container = this.chartContainer.nativeElement;
    this.chartRendered = true;
    container.innerHTML = this.response.chartHtml;
    const scripts = Array.from(container.querySelectorAll("script"));
    scripts.forEach((script) => script.remove());

    for (const oldScript of scripts.filter((script) => script.src)) {
      if (document.querySelector(`script[src="${oldScript.src}"]`)) {
        continue;
      }

      await new Promise<void>((resolve) => {
        const script = document.createElement("script");
        script.src = oldScript.src;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.body.appendChild(script);
      });
    }

    scripts
      .filter((script) => !script.src && script.textContent)
      .forEach((script) => {
        try {
          new Function(script.textContent ?? "")();
        } catch (error) {
          console.error("Error executing dashboard chart script:", error);
        }
      });
  }
}
